import { useState, useEffect, useCallback } from 'react';
import { flujoCajaApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DatePickerInput } from '../ui/date-picker';
import { toast } from '../../hooks/use-toast';
import { Landmark, Pencil, Plus, Trash2, Check, X } from 'lucide-react';
import { fmtFecha } from '../../lib/utils';

const formatCLP = (value) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value || 0);

const todayISO = () => new Date().toISOString().split('T')[0];

export default function BancosPanel({ onChange }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ banco: '', monto: 0 });
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ banco: '', monto: 0, fecha_actualizacion: todayISO() });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await flujoCajaApi.listBancos();
      setItems(res.data.items || []);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error cargando bancos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (b) => {
    setEditingId(b.id);
    setEditForm({ banco: b.banco, monto: b.monto });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({ banco: '', monto: 0 }); };

  const saveEdit = async (b) => {
    setSaving(true);
    try {
      await flujoCajaApi.updateBanco(b.id, {
        banco: editForm.banco,
        monto: Number(editForm.monto) || 0,
        fecha_actualizacion: todayISO(),
      });
      toast({ title: 'Saldo actualizado' });
      cancelEdit();
      await load();
      onChange?.();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const saveNew = async () => {
    if (!newForm.banco.trim()) { toast({ title: 'Nombre requerido', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await flujoCajaApi.createBanco({
        banco: newForm.banco.trim(),
        monto: Number(newForm.monto) || 0,
        fecha_actualizacion: newForm.fecha_actualizacion || todayISO(),
      });
      toast({ title: 'Banco agregado' });
      setCreating(false);
      setNewForm({ banco: '', monto: 0, fecha_actualizacion: todayISO() });
      await load();
      onChange?.();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al crear', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (b) => {
    if (!window.confirm(`¿Eliminar "${b.banco}"?`)) return;
    try {
      await flujoCajaApi.deleteBanco(b.id);
      toast({ title: 'Banco eliminado' });
      await load();
      onChange?.();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const total = items.reduce((a, b) => a + b.monto, 0);

  return (
    <Card data-testid="bancos-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Landmark className="w-4 h-4" /> Detalle Cuentas Bancarias
            </CardTitle>
            <CardDescription>
              Total: <span className="font-semibold text-emerald-700">{formatCLP(total)}</span> · {items.length} cuenta{items.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setCreating(true)} data-testid="btn-new-banco">
            <Plus className="w-4 h-4 mr-1" /> Agregar banco
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-32" />
        ) : items.length === 0 ? (
          <div className="text-center text-muted-foreground text-sm py-6">
            No hay cuentas bancarias. Agrega una con el botón superior.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((b) => {
              const isEditing = editingId === b.id;
              return (
                <div
                  key={b.id}
                  className={`p-3 rounded-lg border transition-colors ${
                    isEditing ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-400 ring-1 ring-amber-300' : 'bg-muted/30'
                  }`}
                  data-testid={`banco-card-${b.id}`}
                >
                  {isEditing ? (
                    <div className="space-y-2">
                      <Input
                        value={editForm.banco}
                        onChange={(e) => setEditForm({ ...editForm, banco: e.target.value })}
                        className="h-8 text-sm"
                        placeholder="Nombre del banco"
                        data-testid={`input-edit-banco-nombre-${b.id}`}
                      />
                      <Input
                        type="number"
                        value={editForm.monto}
                        onChange={(e) => setEditForm({ ...editForm, monto: Number(e.target.value) || 0 })}
                        className="h-8 text-sm text-right tabular-nums"
                        placeholder="Monto"
                        data-testid={`input-edit-banco-monto-${b.id}`}
                      />
                      <div className="flex justify-end gap-1 pt-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={cancelEdit} disabled={saving}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" className="h-7 w-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => saveEdit(b)} disabled={saving} data-testid={`btn-save-banco-${b.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{b.banco}</p>
                          <p className="text-xs text-muted-foreground">
                            Act: {b.fecha_actualizacion ? fmtFecha(b.fecha_actualizacion) : '—'}
                          </p>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => startEdit(b)} data-testid={`btn-edit-banco-${b.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={() => doDelete(b)} data-testid={`btn-del-banco-${b.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className={`mt-2 text-lg font-bold tabular-nums ${b.monto >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCLP(b.monto)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialog crear banco */}
      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent className="max-w-md" data-testid="dialog-new-banco">
          <DialogHeader>
            <DialogTitle>Nuevo banco / cuenta</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Nombre del banco</Label>
              <Input
                value={newForm.banco}
                onChange={(e) => setNewForm({ ...newForm, banco: e.target.value })}
                placeholder="Ej: BChile Cta Corriente"
                data-testid="input-new-banco-nombre"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Saldo (CLP)</Label>
                <Input
                  type="number"
                  value={newForm.monto}
                  onChange={(e) => setNewForm({ ...newForm, monto: Number(e.target.value) || 0 })}
                  data-testid="input-new-banco-monto"
                />
              </div>
              <div>
                <Label>Fecha</Label>
                <DatePickerInput
                  value={newForm.fecha_actualizacion}
                  onChange={(iso) => setNewForm({ ...newForm, fecha_actualizacion: iso })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={saveNew} disabled={saving} data-testid="btn-save-new-banco">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
